
import React from 'react';
import { Search, Filter, X, Plus } from 'lucide-react';

export interface FilterOption {
  label: string;
  value: string;
}

interface FilterBarProps {
  onSearchChange: (value: string) => void;
  searchValue: string;
  searchPlaceholder?: string;
  
  // Tabs (Pills)
  tabs?: FilterOption[];
  activeTab?: string;
  onTabChange?: (value: string) => void;

  // Dropdown Filters (Simplified for now)
  onFilterClick?: () => void;
  
  // Actions
  actionLabel?: string;
  onActionClick?: () => void;
  
  // Extra content (e.g. secondary buttons)
  children?: React.ReactNode;
}

export const FilterBar: React.FC<FilterBarProps> = ({
  onSearchChange,
  searchValue,
  searchPlaceholder = "Search...",
  tabs,
  activeTab,
  onTabChange,
  onFilterClick,
  actionLabel,
  onActionClick,
  children
}) => {
  return (
    <div className="flex flex-col space-y-4 mb-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        {/* Search Input */}
        <div className="relative w-full md:w-96 group">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-brand-500 transition-colors" size={18} />
          <input 
            type="text" 
            placeholder={searchPlaceholder}
            className="w-full pl-10 pr-4 py-2.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl text-sm focus:ring-2 focus:ring-brand-500 focus:border-transparent outline-none transition-shadow shadow-sm"
            value={searchValue}
            onChange={(e) => onSearchChange(e.target.value)}
          />
          {searchValue && (
            <button 
              onClick={() => onSearchChange('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
            >
              <X size={14} />
            </button>
          )}
        </div>

        {/* Actions & Children */}
        <div className="flex items-center space-x-3 w-full md:w-auto">
          {children}
          
          {onFilterClick && (
            <button 
              onClick={onFilterClick}
              className="flex items-center px-4 py-2.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl text-slate-700 dark:text-slate-300 font-medium text-sm hover:bg-slate-50 dark:hover:bg-slate-800 transition shadow-sm"
            >
              <Filter size={16} className="mr-2" /> Filters
            </button>
          )}
          
          {actionLabel && onActionClick && (
            <button 
              onClick={onActionClick}
              className="flex items-center px-4 py-2.5 bg-brand-600 hover:bg-brand-700 text-white rounded-xl font-bold text-sm shadow-sm hover:shadow-md transition"
            >
              <Plus size={18} className="mr-2" /> {actionLabel}
            </button>
          )}
        </div>
      </div>

      {/* Tabs / Pills */}
      {tabs && tabs.length > 0 && (
        <div className="flex items-center space-x-2 overflow-x-auto pb-2 scrollbar-hide">
          {tabs.map(tab => (
            <button
              key={tab.value}
              onClick={() => onTabChange?.(tab.value)}
              className={`
                px-4 py-1.5 rounded-full text-xs font-bold whitespace-nowrap transition-all border
                ${activeTab === tab.value 
                  ? 'bg-slate-900 dark:bg-white text-white dark:text-slate-900 border-transparent shadow-md transform scale-105' 
                  : 'bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-400 border-slate-200 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800'}
              `}
            >
              {tab.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
